import {
    type Components,
    formHelperTextClasses,
    inputAdornmentClasses,
    inputLabelClasses,
    outlinedInputClasses,
    svgIconClasses,
    type Theme,
} from '@mui/material'

export const MuiInputLabel: Components<Theme>['MuiInputLabel'] = {
    defaultProps: {
        shrink: true,
        disableAnimation: true,
    },
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.body2,
            marginBottom: theme.spacing(1),
            color: theme.palette.grey[700],

            // Disables label animation (aka "floating notched label")
            position: 'static',
            transform: 'none',
            transition: 'none',
            maxWidth: 'none',
            [`&.${inputLabelClasses.shrink}`]: {
                transform: 'none',
                maxWidth: 'none',
            },

            [`&.${inputLabelClasses.focused}, &.${inputLabelClasses.error}, &.${inputLabelClasses.disabled}`]: {
                color: theme.palette.grey[700],
            },
        }),
    },
}

export const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
    defaultProps: {
        notched: false,
    },
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.body2,
            display: 'flex',
            minHeight: 32,
            padding: theme.spacing(1, 2),
            alignItems: 'center',
            gap: theme.spacing(1),
            alignSelf: 'stretch',
            borderRadius: 4,
            backgroundColor: theme.palette.common.white,

            [`&:hover:not(.${outlinedInputClasses.focused}, .${outlinedInputClasses.disabled}, .${outlinedInputClasses.error})`]: {
                backgroundColor: theme.palette.common.white,

                [`& .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: theme.palette.grey[400],
                },
            },
            [`&.${outlinedInputClasses.focused}`]: {
                backgroundColor: theme.palette.common.white,
                boxShadow: `0 0 0 2px ${theme.palette.primary[200]}`,

                [`& .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: theme.palette.primary[600],
                    borderWidth: 1,
                },
            },
            [`&.${outlinedInputClasses.disabled}`]: {
                backgroundColor: theme.palette.grey[50],
                color: theme.palette.grey[400],

                [`& .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: theme.palette.grey[200],
                },
                [`& .${inputAdornmentClasses.positionEnd} .${svgIconClasses.root}`]: {
                    color: theme.palette.grey[300],
                },
            },
            [`&.${outlinedInputClasses.error}`]: {
                backgroundColor: theme.palette.common.white,

                [`& .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: theme.palette.error[600],
                },
                [`& .${inputAdornmentClasses.positionStart}`]: {
                    color: theme.palette.error[600],
                },
            },
        }),
        notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.grey[300],
            borderWidth: 1,
            borderRadius: 'inherit',
        }),
        input: ({ theme }) => ({
            ...theme.typography.body2,
            height: 'auto',
            padding: 0,
            color: 'currentColor',

            /**
             * Browsers dim ::placeholder via a default opacity, regardless of `color`.
             * In addition, Chromium/WebKit ignore `color` on disabled inputs, using -webkit-text-fill-color instead.
             */
            '&:disabled, &::placeholder': {
                color: theme.palette.grey[400],
                WebkitTextFillColor: theme.palette.grey[400],
                opacity: 1,
            },
        }),
    },
}

export const MuiInputAdornment: Components<Theme>['MuiInputAdornment'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            color: theme.palette.grey[500],
            marginRight: 0,

            [`&.${inputAdornmentClasses.positionEnd}`]: {
                gap: theme.spacing(1),
            },
        }),
    },
}

export const MuiFormHelperText: Components<Theme>['MuiFormHelperText'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.caption,
            marginTop: theme.spacing(1),
            marginRight: 0,
            marginLeft: 0,
            color: theme.palette.grey[600],

            [`&.${formHelperTextClasses.error}`]: {
                color: theme.palette.error[700],
            },
            [`&.${formHelperTextClasses.disabled}`]: {
                color: theme.palette.grey[600],
            },
        }),
    },
}
